import React from 'react';
import cloud from 'd3-cloud';

const MAX_WORDS = 150;

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || '').trim());
  return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 99, g: 102, b: 241 };
}
function lerpColor(a, b, t) {
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bl = Math.round(a.b + (b.b - a.b) * t);
  return `rgb(${r},${g},${bl})`;
}

/**
 * Word Cloud visual. Words come from a text/category field (`nameKey`); each
 * word's size is driven by a numeric measure (`valueKey`). When no measure is
 * provided, words are sized by how often the category occurs (frequency).
 * Colored from the current theme palette; hovering a word shows its value.
 */
function WordCloudChart({ data = [], nameKey, valueKey, colorKey, colorFrom, colorTo, rotate = false, colors = [], formatValue, height = 340 }) {
  const wrapRef = React.useRef(null);
  const [dims, setDims] = React.useState({ w: 0, h: height });
  const [laid, setLaid] = React.useState([]);
  const [hover, setHover] = React.useState(null);

  // Aggregate rows → { word: {size, color} }. Size sums the size measure (or
  // frequency when none); color sums the color measure so words can be tinted
  // low→high along a two-color gradient.
  const words = React.useMemo(() => {
    const totals = {};
    for (const row of data) {
      const w = String(row?.[nameKey] ?? '').trim();
      if (!w) continue;
      const raw = valueKey != null ? Number(row[valueKey]) : NaN;
      const v = Number.isFinite(raw) ? raw : 1;
      const cRaw = colorKey ? Number(row[colorKey]) : NaN;
      if (!totals[w]) totals[w] = { text: w, value: 0, color: 0, hasColor: false };
      totals[w].value += v;
      if (Number.isFinite(cRaw)) { totals[w].color += cRaw; totals[w].hasColor = true; }
    }
    return Object.values(totals)
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_WORDS);
  }, [data, nameKey, valueKey, colorKey]);

  // Track container size.
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0].contentRect;
      setDims({ w: Math.max(0, Math.floor(cr.width)), h: Math.max(120, Math.floor(cr.height)) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Run the d3-cloud layout whenever words or size change.
  React.useEffect(() => {
    const { w, h } = dims;
    if (!w || !h || words.length === 0) { setLaid([]); return; }
    const vals = words.map((d) => d.value);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const minF = 13;
    const maxF = Math.max(30, Math.min(w, h) / 5);
    const fontFor = (v) => {
      if (max === min) return (minF + maxF) / 2;
      const t = Math.sqrt((v - min) / (max - min));
      return Math.round(minF + t * (maxF - minF));
    };
    let cancelled = false;
    const layout = cloud()
      .size([w, h])
      .words(words.map((d) => ({ text: d.text, value: d.value, color: d.color, hasColor: d.hasColor, size: fontFor(d.value) })))
      .padding(3)
      .rotate(() => (rotate && Math.random() < 0.25 ? 90 : 0))
      .font('Inter, system-ui, sans-serif')
      .fontSize((d) => d.size)
      .on('end', (out) => { if (!cancelled) setLaid(out); });
    layout.start();
    return () => { cancelled = true; layout.stop(); };
  }, [words, dims, rotate]);

  const palette = colors && colors.length ? colors : ['#6366f1', '#22d3ee', '#f59e0b', '#ef4444', '#10b981', '#a855f7'];

  // When a color measure is present, map each word to a color interpolated
  // between the low and high gradient endpoints by its color value.
  const gradientActive = !!colorKey && words.some((d) => d.hasColor);
  const wordColor = React.useMemo(() => {
    if (!gradientActive) return null;
    const from = hexToRgb(colorFrom || '#6366f1');
    const to = hexToRgb(colorTo || '#ef4444');
    const cvals = words.filter((d) => d.hasColor).map((d) => d.color);
    const min = Math.min(...cvals);
    const max = Math.max(...cvals);
    const map = {};
    for (const d of words) {
      const t = max === min ? 0.5 : (d.color - min) / (max - min);
      map[d.text] = d.hasColor ? lerpColor(from, to, t) : (colorFrom || '#6366f1');
    }
    return map;
  }, [gradientActive, words, colorFrom, colorTo]);

  return (
    <div ref={wrapRef} style={{ width: '100%', height: '100%', minHeight: 120, position: 'relative' }} data-testid="wordcloud-chart">
      {words.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-8">Add a text field to show a word cloud</p>
      ) : (
        <svg width={dims.w} height={dims.h} style={{ display: 'block' }}>
          <g transform={`translate(${dims.w / 2},${dims.h / 2})`}>
            {laid.map((d, i) => (
              <text
                key={`${d.text}-${i}`}
                textAnchor="middle"
                transform={`translate(${d.x},${d.y}) rotate(${d.rotate})`}
                style={{ fontSize: d.size, fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fill: (wordColor && wordColor[d.text]) || palette[i % palette.length], cursor: 'default', userSelect: 'none' }}
                onMouseEnter={(e) => setHover({ text: d.text, value: d.value, color: d.color, hasColor: d.hasColor, x: e.clientX, y: e.clientY })}
                onMouseMove={(e) => setHover((h) => (h ? { ...h, x: e.clientX, y: e.clientY } : h))}
                onMouseLeave={() => setHover(null)}
              >
                {d.text}
              </text>
            ))}
          </g>
        </svg>
      )}
      {hover && (
        <div
          className="pointer-events-none fixed z-[300] rounded-md px-2.5 py-1.5 text-[11px] shadow-lg"
          style={{ left: hover.x + 12, top: hover.y + 12, background: 'rgba(17,17,27,0.95)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', minWidth: 120 }}
          data-testid="wordcloud-tooltip"
        >
          <div className="font-semibold mb-0.5">{hover.text}</div>
          <div className="flex items-center justify-between gap-3 opacity-90">
            <span className="opacity-60">Size{valueKey ? ` · ${valueKey}` : ''}</span>
            <span className="font-medium">{formatValue ? formatValue(hover.value, valueKey || 'value') : hover.value}</span>
          </div>
          {colorKey && (
            <div className="flex items-center justify-between gap-3 opacity-90 mt-0.5">
              <span className="opacity-60">Color · {colorKey}</span>
              <span className="font-medium">{hover.hasColor ? (formatValue ? formatValue(hover.color, colorKey) : hover.color) : '—'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(WordCloudChart);
